import React, { useCallback, useEffect, useMemo, useState,useContext } from "react";
import { Modal, Box } from "@mui/material";
import {
  motion,
  useMotionValue,
  useTransform,
  animate,
  useAnimation,
} from "framer-motion";
import InfinityCard from "./InfinityCard";
import CharacterCard from "./CharacterCard";
import { MainContext } from "../App";

const CARD_W = 320;
const CARD_H = 480;
const HERO_SCALE = 1.5; // big cinematic zoom
const HERO_RX = -16; // subtle top tilt
const HERO_RY = 14; // + = right edge closer (left edge away)
const HERO_LIFT = 8; // pixels up
const HERO_IN_D = 0.55; // seconds in
const HERO_OUT_D = 0.15; // seconds out
const HERO_HOLD = 2000; // ms hold


// Slot transforms (depth layering)
const SLOTS = {
  active: { x: 0, y: 0, scale: 1.0, z: 30, opacity: 1.0, rotate: 0 },
  next: { x: 18, y: 14, scale: 0.965, z: 20, opacity: 1, rotate: 5 }, // slight clockwise
  back: { x: -12, y: 28, scale: 0.92, z: 10, opacity: 1, rotate: -5 }, // slight counter-clockwise
};

// Drag commit tuning
const COMMIT_DIST = 110; // px: drag distance threshold
const COMMIT_SPEED = 650; // px/s: velocity threshold
const BACK_NUDGE = 28; // px: directional nudge toward back slot at commit

const CardFront = ({ card, height }) =>
  card.type === "Infinity" ? (
    <InfinityCard {...card} heightPer={100} containerHeight={height} />
  ) : (
    <CharacterCard {...card} heightPer={100} containerHeight={height} />
  );

const wrap = (i, len) => (len ? (i + len) % len : 0);

const SwipeInspectModal = ({ open, onClose, cards, startIndex = 0 }) => {
  const {deviceType} = useContext(MainContext)
  const isMobile = deviceType === 'mobile'
  const deck = useMemo(() => cards.filter(Boolean), [cards]);
  const [index, setIndex] = useState(startIndex);
  const [lowerZ, setLowerZ] = useState(false); // briefly drop active beneath "next" during commit

  // Motion values for ACTIVE card
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const rx = useMotionValue(0); // rotateX (slight top tilt)
  const ry = useMotionValue(0); // rotateY (negative = left edge closer)
  const ripple1 = useAnimation();
  const ripple2 = useAnimation();

  const s = useMotionValue(1);
  const rotate = useTransform(x, (v) => v / 28);
  const shadowOpacity = useTransform(
    y,
    (v) => 0.35 + Math.min(Math.abs(v) / 520, 0.22)
  );
  const cancelAutoRef = React.useRef(() => {});
  const activeIdx = wrap(index, deck.length);
  const activeIsCharacter = deck[activeIdx]?.type !== "Infinity"; // your code treats non-Infinity as Character

  const triggerRipples = async () => {
    // reset instantly
    ripple1.set({ scale: 0.2, opacity: 0.9 });
    ripple2.set({ scale: 0.2, opacity: 0.9 });

    // blast out (scale big so it races off-screen)
    const opts = { duration: 0.6, ease: "easeOut" };
    await Promise.all([
      ripple1.start({ scale: 10, opacity: 0, ...opts }), // first ring
      ripple2.start({
        scale: 12,
        opacity: 0,
        transition: { ...opts, delay: 0.06 },
      }), // echo
    ]);
  };

  // Run the hero pose on active Character
  useEffect(() => {
    if (!open || !deck.length || !activeIsCharacter) return;

    let cancelled = false;

    // Define a cancel that stops all tweens
    cancelAutoRef.current = () => {
      cancelled = true;
      x.stop();
      y.stop();
      s.stop();
      rx.stop();
      ry.stop();
    };

    const run = async () => {
      try {
        await Promise.all([
          animate(s, HERO_SCALE, { duration: HERO_IN_D, ease: "easeOut" }),
          animate(rx, HERO_RX, { duration: HERO_IN_D, ease: "easeOut" }),
          animate(ry, HERO_RY, { duration: HERO_IN_D, ease: "easeOut" }), // RIGHT corner forward
          animate(y, HERO_LIFT, { duration: HERO_IN_D, ease: "easeOut" }),
        ]);
        if (cancelled) return;

        await new Promise((r) => setTimeout(r, HERO_HOLD));
        if (cancelled) return;

        triggerRipples();

        await Promise.all([
          animate(s, 1, { duration: HERO_OUT_D, ease: "easeInOut" }),
          animate(rx, 0, { duration: HERO_OUT_D, ease: "easeInOut" }),
          animate(ry, 0, { duration: HERO_OUT_D, ease: "easeInOut" }),
          animate(y, 0, { duration: HERO_OUT_D, ease: "easeInOut" }),
        ]);
      } finally {
        cancelAutoRef.current = () => {
          cancelled = true;
          x.stop();
          y.stop();
          s.stop();
          rx.stop();
          ry.stop();
          ripple1.stop();
          ripple2.stop();
        };
      }
    };

    run();

    // Cleanup on index/type change or modal close
    return () => cancelAutoRef.current();
  }, [open, activeIdx, activeIsCharacter, deck.length, x, y, s, rx, ry]);

  useEffect(() => {
    if (!open) return;
    setIndex(wrap(startIndex, deck.length || 1));
    x.set(0);
    y.set(0);
    s.set(1);
    setLowerZ(false);
  }, [open, startIndex, deck.length, x, y, s]);

  const next = useCallback(
    () => setIndex((i) => wrap(i + 1, deck.length)),
    [deck.length]
  );
  const prev = useCallback(
    () => setIndex((i) => wrap(i - 1, deck.length)),
    [deck.length]
  );

  // Keyboard
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, next, prev, onClose]);

  const commitToBack = async (dx, dy) => {
    cancelAutoRef.current?.();
    const mag = Math.hypot(dx, dy) || 1;
    const ux = dx / mag;
    const uy = dy / mag;

    // animate active toward back slot with a slight nudge in drag direction
    const targetX = SLOTS.back.x + ux * BACK_NUDGE;
    const targetY = SLOTS.back.y + uy * BACK_NUDGE;

    // let the "next" card visually come above midway through the commit
    setTimeout(() => setLowerZ(true), 80);

    await Promise.all([
      animate(x, targetX, { duration: 0.24, ease: "easeOut" }),
      animate(y, targetY, { duration: 0.24, ease: "easeOut" }),
      animate(s, SLOTS.back.scale, { duration: 0.24, ease: "easeOut" }),
    ]);

    next(); // rotate the deck
    setLowerZ(false); // restore z layer for the new active card
    x.set(0);
    y.set(0);
    s.set(1);
  };

  const onDragEnd = (_e, info) => {
    const { offset, velocity } = info;
    const dist = Math.hypot(offset.x || 0, offset.y || 0);
    const speed = Math.hypot(velocity.x || 0, velocity.y || 0);

    if (dist > COMMIT_DIST || speed > COMMIT_SPEED) {
      commitToBack(offset.x, offset.y);
    } else {
      // snap back
      animate(x, 0, { type: "spring", stiffness: 520, damping: 40 });
      animate(y, 0, { type: "spring", stiffness: 520, damping: 40 });
      animate(s, 1, { type: "spring", stiffness: 520, damping: 40 });
      setLowerZ(false);
    }
  };

  // const activeIdx = wrap(index, deck.length);
  const nextIdx = wrap(index + 1, deck.length);
  const backIdx = wrap(index + 2, deck.length);

  const CardPreview = ({ slot, card }) => {
    const t = SLOTS[slot];
    return (
      <motion.div
        style={{
          position: "absolute",
          width: isMobile ? CARD_W *.65 : CARD_W,
          height: isMobile ?  CARD_H *.65 : CARD_H,
          transformOrigin: "center",
          zIndex: t.z,
          borderRadius: 12,
          pointerEvents: "none",
        }}
        animate={{
          x: t.x,
          y: t.y,
          scale: t.scale,
          // opacity: t.opacity,
          rotate: t.rotate,
        }}
        transition={{ duration: 0.22, ease: "easeOut" }}
      >
        {card && <CardFront card={card} height={isMobile ?  CARD_H *.65 : CARD_H} />}
      </motion.div>
    );
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: "fixed",
          inset: 0,
          backdropFilter: "blur(6px)",
          background:
            "radial-gradient(80% 60% at 50% 0%, rgba(124,77,255,0.28) 0%, rgba(0, 0, 0, 0.78) 70%, rgba(0,0,0,0.92) 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 2,
        }}
      >
        <Box
          sx={{
            position: "relative",
            width: isMobile ? CARD_W *.65 : CARD_W,
          height: isMobile ?  CARD_H *.65 : CARD_H,
            perspective: 520,
            overflow: "visible",
          }}
        >
          {/* soft inner glow + shadow */}
          <motion.div
            style={{
              position: "absolute",
              inset: 0,
              pointerEvents: "none",
              borderRadius: 16,
              boxShadow: `0 24px 60px rgba(0,0,0,${shadowOpacity.get()}), 0 0 72px rgba(124,77,255,0.24) inset`,
            }}
          />

          {/* BACK + NEXT previews */}
          <CardPreview slot="back" card={deck[backIdx]} />
          <CardPreview slot="next" card={deck[nextIdx]} />

          {/* ACTIVE (top) – free 2D drag with transparent drag shield */}
          <motion.div
            // DRAG SETTINGS: more travel, momentum, no constraints
            drag
            dragElastic={0.28}
            dragMomentum={0.6}
            onDragEnd={onDragEnd}
            style={{
              position: "absolute",
              width: isMobile ? CARD_W *.65 : CARD_W,
          height: isMobile ?  CARD_H *.65 : CARD_H,
              x,
              y,
              scale: s,
              rotate,
              rotateX: rx, // NEW
              rotateY: ry, // NEW
              // briefly drop below next during commit to sell the stack move
              zIndex: lowerZ ? SLOTS.back.z : SLOTS.active.z,
              borderRadius: 12,
              cursor: "grab",
              userSelect: "none",
            }}
            whileTap={{ cursor: "grabbing" }}
            initial={false}
          >
            <RippleLayer ripple1={ripple1} ripple2={ripple2} />

            {/* The visible card content (non-interactive during drag) */}
            <div
              style={{ position: "absolute", inset: 0, pointerEvents: "none" }}
            >
              {deck[activeIdx] && <CardFront card={deck[activeIdx]}  height={isMobile ?  CARD_H *.65 : CARD_H}  />}
            </div>

            {/* Transparent drag shield (captures pointer so images/text don't drag) */}
            <motion.div
              style={{
                position: "absolute",
                inset: 0,
                background: "transparent",
                zIndex: 2,
              }}
            />
          </motion.div>
        </Box>

        {/* Controls */}
        <Box sx={{ position: "absolute", bottom: 24, display: "flex", gap: 2 }}>
          <button
            onClick={() => commitToBack(-COMMIT_DIST, 0)} // tap to send left/back
            aria-label="Previous"
            style={{
              padding: "10px 14px",
              borderRadius: 12,
              background: "rgba(255,255,255,0.08)",
              border: "1px solid rgba(255,255,255,0.12)",
              color: "#fff",
              fontWeight: 700,
            }}
          >
            ←
          </button>
          <button
            onClick={onClose}
            style={{
              padding: "10px 18px",
              borderRadius: 12,
              background:
                "linear-gradient(180deg, rgba(124,77,255,0.9) 0%, rgba(100,42,255,0.9) 100%)",
              color: "#fff",
              border: 0,
              fontWeight: 800,
              letterSpacing: 0.3,
            }}
          >
            Close
          </button>
          <button
            onClick={() => commitToBack(COMMIT_DIST, 0)} // tap to send right/back
            aria-label="Next"
            style={{
              padding: "10px 14px",
              borderRadius: 12,
              background: "rgba(255,255,255,0.08)",
              border: "1px solid rgba(255,255,255,0.12)",
              color: "#fff",
              fontWeight: 700,
            }}
          >
            →
          </button>
        </Box>
      </Box>
    </Modal>
  );
};

const RippleLayer = ({ ripple1, ripple2 }) => {
  // base size is small; we scale to blow past screen
  const BASE = 80; // px

  const ringStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    width: BASE,
    height: BASE,
    marginLeft: -BASE / 2,
    marginTop: -BASE / 2,
    borderRadius: "9999px",
    border: "1px solid rgba(255,255,255,1)",
    pointerEvents: "none",
    zIndex: 1, // <-- behind the card (card content uses zIndex:2)
    mixBlendMode: "screen", // optional: makes white punchy on dark bg
  };

  return (
    <>
      <motion.div initial={{opacity:0}} style={ringStyle} animate={ripple1} />
      <motion.div initial={{opacity:0}}  style={ringStyle} animate={ripple2} />
    </>
  );
};

export default SwipeInspectModal;
